import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import { useEffect, useRef, useState } from "react";

import { useDataSources } from "../../api/dataSources";
import { type FilestoreFile, parseFilestoreFile } from "../../api/filestore";
import {
  type ProjectFolder,
  type SetFolderBoundaryOutcome,
  useClearFolderBoundary,
  useSetFolderBoundary,
  useUpdateFolder,
} from "../../api/projectFolders";
import { type BoundaryStyle, DEFAULT_FOLDER_BOUNDARY_STYLE, resolveBoundaryStyle } from "../../types/boundaryStyle";
import FilestoreBrowser from "../FilestoreBrowser";
import BoundaryStyleEditor from "./BoundaryStyleEditor";

interface FolderBoundaryDialogProps {
  open: boolean;
  /** The folder, as the list currently has it (so a change shows straight away). */
  folder: ProjectFolder | null;
  onClose: () => void;
}

type Source = "file" | "data_source";

interface FilePreview {
  polygons: number;
  skipped: number;
}

/** How many polygon features a parsed file holds, and how many it does not. */
function previewGeojson(geojson: unknown): FilePreview {
  const features: unknown[] = Array.isArray((geojson as { features?: unknown[] })?.features)
    ? ((geojson as { features: unknown[] }).features ?? [])
    : [];
  let polygons = 0;
  features.forEach((raw) => {
    const type = (raw as { geometry?: { type?: string } })?.geometry?.type;
    if (type === "Polygon" || type === "MultiPolygon") polygons += 1;
  });
  return { polygons, skipped: features.length - polygons };
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Sliders move faster than the server needs to hear about. */
const STYLE_SAVE_DELAY_MS = 250;

const errorMessage = (e: unknown, fallback: string): string =>
  (e as { message?: string } | undefined)?.message || fallback;

/**
 * Give a folder its outline on the listing map: the estate, region or tenure
 * it stands for. From a file (zipped shapefile, KML, GeoJSON — the same kinds
 * the property import takes) or from a property boundary data source, whose
 * parcels over the folder's projects become the boundary. Shows what the
 * folder has now and can remove it.
 */
export default function FolderBoundaryDialog({ open, folder, onClose }: FolderBoundaryDialogProps) {
  const [source, setSource] = useState<Source>("file");
  const [browserOpen, setBrowserOpen] = useState(false);
  const [file, setFile] = useState<FilestoreFile | null>(null);
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [dataSourceId, setDataSourceId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<SetFolderBoundaryOutcome | null>(null);
  const [removed, setRemoved] = useState(false);

  const { dataSources, isLoading: sourcesLoading } = useDataSources("property_boundary");
  const setBoundary = useSetFolderBoundary();
  const clearBoundary = useClearFolderBoundary();
  const busy = setBoundary.isPending || clearBoundary.isPending;

  // How the boundary is drawn. Edits preview on the map at once (the folder
  // update is optimistic on the folder list) and save a moment after the
  // last change. Its own mutation instance so a save can't swallow another's callbacks.
  const updateStyle = useUpdateFolder();
  const [style, setStyle] = useState<BoundaryStyle>(DEFAULT_FOLDER_BOUNDARY_STYLE);
  const styleTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (styleTimer.current !== null) window.clearTimeout(styleTimer.current);
    },
    []
  );
  const saveStyle = (next: BoundaryStyle | null, delay: number) => {
    if (!folder) return;
    const folderId = folder.id;
    if (styleTimer.current !== null) window.clearTimeout(styleTimer.current);
    styleTimer.current = window.setTimeout(() => {
      styleTimer.current = null;
      updateStyle.mutate({ folderId, boundary_style: next });
    }, delay);
  };

  // Fresh each time it opens, for whichever folder.
  useEffect(() => {
    if (!open) return;
    setSource("file");
    setFile(null);
    setPreview(null);
    setPreviewing(false);
    setDataSourceId("");
    setError(null);
    setOutcome(null);
    setRemoved(false);
    setStyle(resolveBoundaryStyle(folder?.boundary_style, DEFAULT_FOLDER_BOUNDARY_STYLE));
    // Only when it opens, or for another folder: the list's copy of the
    // style changes under us as our own saves land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, folder?.id]);

  const handleFile = async (picked: FilestoreFile) => {
    setFile(picked);
    setPreview(null);
    setError(null);
    setOutcome(null);
    setPreviewing(true);
    try {
      const next = previewGeojson(await parseFilestoreFile(picked.id));
      setPreview(next);
      if (next.polygons === 0) {
        setError("That file has no polygon features. A boundary must be made of polygons.");
      }
    } catch {
      setError("Couldn't read that file. Shapefiles must be zipped; KML and GeoJSON also work.");
    } finally {
      setPreviewing(false);
    }
  };

  const submit = () => {
    if (!folder) return;
    setError(null);
    setOutcome(null);
    setRemoved(false);
    const input =
      source === "file"
        ? file
          ? { folderId: folder.id, filestoreFileId: file.id }
          : null
        : dataSourceId !== ""
          ? { folderId: folder.id, dataSourceId }
          : null;
    if (!input) return;
    setBoundary.mutate(input, {
      onSuccess: (res) => setOutcome(res?.data?.result ?? null),
      onError: (e) => setError(errorMessage(e, "Couldn't set the boundary. Please try again.")),
    });
  };

  const remove = () => {
    if (!folder) return;
    setError(null);
    setOutcome(null);
    clearBoundary.mutate(folder.id, {
      onSuccess: () => setRemoved(true),
      onError: (e) => setError(errorMessage(e, "Couldn't remove the boundary. Please try again.")),
    });
  };

  const hasBoundary = !!folder?.boundary_bbox;
  const canSubmit =
    !busy &&
    !!folder &&
    (source === "file" ? !!file && !previewing && (preview?.polygons ?? 0) > 0 : dataSourceId !== "");
  const done = !!outcome;

  const provenance = folder?.boundary_source_name
    ? `from the data source “${folder.boundary_source_name}”`
    : folder?.boundary_file_name
      ? `from ${folder.boundary_file_name}`
      : "";
  const updated = folder?.boundary_updated_at ? new Date(folder.boundary_updated_at).toLocaleDateString() : null;

  return (
    <>
      <FilestoreBrowser
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onSelectFile={(picked) => void handleFile(picked)}
        acceptedFileTypes=".zip,.kml,.geojson,.json"
      />
      <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PentagonOutlinedIcon sx={{ color: hasBoundary ? style.color : DEFAULT_FOLDER_BOUNDARY_STYLE.color }} />
          {folder ? `Boundary for “${folder.name}”` : "Folder boundary"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <Typography variant="body2" color="text.secondary">
            A folder&apos;s boundary is drawn on the map around its projects: the estate, region or
            area the folder stands for. Opening the folder shows it, along with the boundaries of the
            folders inside it.
          </Typography>

          {hasBoundary && folder && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 1.5,
                border: "1px solid #fcd34d",
                backgroundColor: "rgba(245, 158, 11, 0.06)",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Current boundary
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {plural(folder.boundary_feature_count, "polygon")}
                  {provenance ? ` ${provenance}` : ""}
                  {updated ? ` · set ${updated}` : ""}
                </Typography>
              </Box>
              <Button size="small" color="error" onClick={remove} disabled={busy}>
                {clearBoundary.isPending ? <CircularProgress size={16} color="inherit" /> : "Remove"}
              </Button>
            </Box>
          )}

          {hasBoundary && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Style
              </Typography>
              <BoundaryStyleEditor
                value={style}
                defaults={DEFAULT_FOLDER_BOUNDARY_STYLE}
                disabled={busy}
                onChange={(next) => {
                  setStyle(next);
                  saveStyle(next, STYLE_SAVE_DELAY_MS);
                }}
                onReset={() => {
                  setStyle(DEFAULT_FOLDER_BOUNDARY_STYLE);
                  saveStyle(null, 0);
                }}
              />
              {updateStyle.isError && (
                <Typography variant="caption" color="error">
                  {updateStyle.error?.message || "Couldn't save the style. Please try again."}
                </Typography>
              )}
            </Box>
          )}

          {removed && !hasBoundary && <Alert severity="info">Boundary removed.</Alert>}

          {outcome ? (
            <Alert severity="success">
              Boundary set from {plural(outcome.feature_count, "polygon")}
              {outcome.clipped_to_projects ? " around the folder's projects" : ""}.
              {outcome.warnings.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {outcome.warnings.map((w) => (
                    <li key={w}>
                      <Typography variant="caption">{w}</Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          ) : (
            <>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {hasBoundary ? "Replace it with" : "Set it from"}
                </Typography>
                <RadioGroup
                  row
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value as Source);
                    setError(null);
                  }}
                >
                  <FormControlLabel value="file" control={<Radio size="small" />} label="A file" />
                  <FormControlLabel
                    value="data_source"
                    control={<Radio size="small" />}
                    label="A property boundary data source"
                  />
                </RadioGroup>
              </Box>

              {source === "file" ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Zipped shapefile (.zip), KML or GeoJSON. Polygon features make up the boundary;
                    other geometry types are skipped.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setBrowserOpen(true)}
                    disabled={busy}
                    sx={{ textTransform: "none", justifyContent: "flex-start" }}
                  >
                    {file ? file.original_filename : "Browse my files…"}
                  </Button>
                  {previewing && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption">Reading file…</Typography>
                    </Box>
                  )}
                  {preview && (
                    <Typography variant="body2">
                      {plural(preview.polygons, "polygon")} found
                      {preview.skipped > 0 ? ` (${preview.skipped} non-polygon features skipped)` : ""}.
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {sourcesLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading data sources…
                    </Typography>
                  ) : dataSources.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No property boundary data sources are set up. Add one under Property Boundaries
                      in the Data Management console, or use a file instead.
                    </Typography>
                  ) : (
                    <TextField
                      select
                      size="small"
                      label="Data source"
                      value={dataSourceId}
                      onChange={(e) => {
                        setDataSourceId(e.target.value === "" ? "" : Number(e.target.value));
                        setError(null);
                      }}
                      disabled={busy}
                    >
                      {dataSources.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    The parcels that overlap this folder&apos;s projects are used. A folder with no
                    located projects yet takes the whole source. Up to 5,000 polygons.
                  </Typography>
                </Box>
              )}
            </>
          )}

          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={onClose} disabled={busy}>
            {done ? "Done" : "Cancel"}
          </Button>
          {!done && (
            <Button variant="contained" onClick={submit} disabled={!canSubmit}>
              {setBoundary.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : hasBoundary ? (
                "Replace boundary"
              ) : (
                "Set boundary"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
